/*
 * Folds the auto-added default pathname exclusions behind a collapsed expander, so a dozen
 * pre-filled patterns do not bury the entries the user added themselves. Display grouping only:
 * the rows stay in the one draft list and each removes by its index there.
 */
import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { ExclusionEntryRow, type IndexedExclusionEntry } from 'src/components/box-folders/ExclusionEntryRow';
import { type ExclusionEntry } from 'src/utils/exclusionEntries';
import { type PathnameMatch } from 'src/utils/pathnameMatchMode';

interface DefaultExclusionsGroupProps {
  entries: readonly IndexedExclusionEntry[];
  getEntryMatch?: (entry: ExclusionEntry) => PathnameMatch;
  isDisabled: boolean;
  /** Test ids are `${testIdPrefix}-defaults-group`, `-defaults-toggle`, and `-defaults-note`. */
  testIdPrefix: string;
  onRemove: (index: number) => void;
}

export const DefaultExclusionsGroup: React.FC<DefaultExclusionsGroupProps> = ({
  entries,
  getEntryMatch,
  isDisabled,
  testIdPrefix,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (entries.length === 0) return null;

  return (
    <Column data-testid={`${testIdPrefix}-defaults-group`}>
      <Expander
        data-testid={`${testIdPrefix}-defaults-toggle`}
        open={isOpen}
        spacingInset="200 none none none"
        title={t('box_folder_exclusions_defaults_heading', { count: entries.length })}
        type="inline"
        onChange={setIsOpen}
      >
        <Column spacing="200">
          <Text color="secondary" data-testid={`${testIdPrefix}-defaults-note`} type="b200">
            {t('box_folder_exclusions_defaults_note')}
          </Text>
          {entries.map(({ entry, index }) => (
            <ExclusionEntryRow
              entry={entry}
              index={index}
              isDisabled={isDisabled}
              key={`${entry.value}-${index}`}
              match={getEntryMatch?.(entry)}
              testIdPrefix={testIdPrefix}
              onRemove={() => { onRemove(index); }}
            />
          ))}
        </Column>
      </Expander>
    </Column>
  );
};
